import React, { useRef, useReducer, useState, useCallback } from "react";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";
import classNames from "classnames";

import {
  useElements,
  CardElement as CardElementStripe,
  CardNumberElement as CardNumberElementStripe,
} from "@stripe/react-stripe-js";

import { remoteConfigSelector } from "@web-solutions/core/store/remote-config/selectors";

import {
  SUBMIT_BUTTON_COLOR,
  SUBMIT_BUTTON_COLOR_LIGHT_THEME,
  SUBMIT_BUTTON_COLOR_DARK_THEME,
} from "src/constants/styles";

import CardElement from "../card-element";
import { PaymentSystem } from "../../constants";
import { getPaymentSystem } from "../../utils/storage";
import validate from "../../utils/validation";
import usePayment from "../../hooks/use-payment";
import { ReactComponent as LoadingIcon } from '../card-form/loading-white.svg';
import { ReactComponent as LockIcon } from '../card-form/lock.svg';

import styles from "./styles.module.css";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";
import { t } from "@web-solutions/module-localization";

const REQUIRED_RECURLY_FIELDS = ["number", "month", "year"];

/**
 * Stripe checks the card fields in the browser and answers instantly — nothing
 * is sent, nothing is charged. Those answers come back as `validation_error`,
 * or as an `incomplete_*` code on an empty/half-typed field.
 */
const isClientSideValidationError = (error) =>
  error?.type === "validation_error" ||
  error?.code === "validation" ||
  (typeof error?.code === "string" && error.code.startsWith("incomplete_"));

const getSubmitButtonColor = (theme) => {
  if (theme === "light") {
    return SUBMIT_BUTTON_COLOR_LIGHT_THEME;
  }

  if (theme === "dark") {
    return SUBMIT_BUTTON_COLOR_DARK_THEME;
  }

  return SUBMIT_BUTTON_COLOR;
};

const formReducer = (state, e) => {
  const { value } = e;
  return {
    ...state,
    [e.name]: {
      ...state[e.name],
      value,
      isValid: validate(value, e.name, 2),
      isTouched: e.touch,
    },
  };
};

const CardForm = ({
  isSimpleView,
  color,
  paymentSystem,
  product,
  onSubmit,
  onChange,
  onReady,
  onSuccess,
  onError,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onUserInfoChange,
  onEvent,
  formClassName,
  buttonClassName,
  fieldClassName,
  buttonTitle,
  additionalFields,
  options,
  inputStyles,
  labelStyles,
  notEmptyLabelStyles,
  children,
  onValidateChildren,
  onSaveTokenOneTime,
}) => {
  paymentSystem = paymentSystem || getPaymentSystem();

  const formRef = useRef();

  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isInvalidCardForm, setInvalidCardForm] = useState(false);

  const {newPaymentModal} = useRemoteConfig();

  const { theme } = useSelector(remoteConfigSelector);
  const submitButtonColor = getSubmitButtonColor(theme?.mode);
  const payment = usePayment();
  const { paymentPopup } = useSelector(remoteConfigSelector);

  const elements = paymentSystem === PaymentSystem.STRIPE && useElements();

  if (paymentSystem === PaymentSystem.SOLIDGATE) {
    options = options || {};
    options.formParams = options.formParams || {};
    options.formParams.submitButtonText = buttonTitle;
    options.formParams.submitButtonColor = isSimpleView
      ? color
      : submitButtonColor;
    if (additionalFields?.cardholder) {
      options.formParams.isCardHolderVisible = true;
    }
    if (additionalFields?.zip) {
      options.formParams.allowedAdditionalFields = ["ZIP"];
      delete additionalFields?.zip;
    }
  }

  const initialState = Object.keys(additionalFields).reduce((acc, key) => {
    if (key === "cardholder") {
      return { ...acc };
    }
    return {
      ...acc,
      [key]: {
        value: "",
        ...additionalFields[key],
        isValid: validate(additionalFields[key].value || "", key),
        isTouched: false,
      },
    };
  }, {});

  const [fields, setFormData] = useReducer(formReducer, initialState);

  const handleInputChange = useCallback((e) => {
    e.preventDefault();
    const { value } = e.target;
    const { name } = e.target;

    setFormData({
      name,
      value,
      touch: true,
    });

    if (typeof onChange === "function") onChange(e);
  }, []);

  const validateForm = () => {
    Object.keys(fields).map((key) => {
      setFormData({
        name: key,
        value: fields[key].value,
        touch: true,
      });
      return key;
    });
  };

  const handleSubmitError = (error) => {
    console.log("[SUBMIT ERROR]", error);

    if (error.fields) {
      error.fields.map((key) => {
        if (REQUIRED_RECURLY_FIELDS.includes(key) && !isInvalidCardForm) {
          setInvalidCardForm(true);
        }

        return key;
      });
    }

    if (error.type === "validation_error" || error.code === "validation") {
      setInvalidCardForm(true);
    }

    return true;
  };

  const handleSubmit = async (e) => {
    if (e.preventDefault) e.preventDefault();
  
    if (isLoading) return;
  
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 1000);
    setLoading(true);
  
    const { id } = product;
    const formData = {
      price_id: id,
      email: fields.email?.value,
      first_name: document.querySelector("input[name=first_name]")?.value,
      last_name: document.querySelector("input[name=last_name]")?.value,
      method: "card",
      paymentSystem,
    };
  
    validateForm();
  
    try {
      if (paymentSystem === PaymentSystem.RECURLY) {
        await new Promise((resolve, reject) => {
          payment.token(formRef.current, async (error, token) => {
            const isValidatedChildren = children ? onValidateChildren() : true;
  
            if (error) {
              handleSubmitError(error);
              return reject(error);
            }
  
            formData.token_id = token?.id;
            onSaveTokenOneTime && onSaveTokenOneTime(formData.token_id);
  
            if (isValidatedChildren) {
              try {
                await onSubmit(formData);
                resolve();
              } catch (submitError) {
                reject(submitError);
              }
            } else {
              resolve();
            }
          });
        });
      } else if (paymentSystem === PaymentSystem.STRIPE) {
        // Stripe billing_details only accepts: name, email, phone, address.
        // Our `fields` keeps first_name/last_name as separate inputs — combine
        // them into a single `name` and drop anything Stripe doesn't recognize.
        const firstName = fields.first_name?.value?.trim();
        const lastName = fields.last_name?.value?.trim();
        const fullName = [firstName, lastName].filter(Boolean).join(' ');
        const billingDetails = {};
        if (fullName) billingDetails.name = fullName;
        if (fields.email?.value) billingDetails.email = fields.email.value;
        if (fields.phone?.value) billingDetails.phone = fields.phone.value;

        let sessionRadar = {};
        try {
          const { radarSession } = await payment.createRadarSession();
          sessionRadar = radarSession;
        } catch (err) {
          // Ignore radar session errors
        }

        // Split form: passing the CardNumberElement is enough — Stripe pulls
        // the linked expiry/CVC from the same Elements instance. Combined form
        // (split disabled): fall back to the single CardElement.
        const cardElement =
          elements.getElement(CardNumberElementStripe) ||
          elements.getElement(CardElementStripe);
        const { error, paymentMethod } = await payment.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: billingDetails,
          radar_options: {
            session: sessionRadar?.id || "",
          },
        });
  
        if (error) {
          handleSubmitError(error);

          // An unfilled or malformed field never left the browser — Stripe
          // checked it locally and handleSubmitError already highlighted it in
          // red. That is the whole feedback the user needs; a "payment failed"
          // popup on top of it would be a lie, since nothing was charged.
          //
          // Anything else here is a real refusal (a bad card number the issuer
          // rejected, say), so it still goes to the popup with its reason —
          // swallowing those would leave the user with a red field and no
          // explanation.
          if (!isClientSideValidationError(error)) {
            handleError({ ...error, forcePopup: true });
          }
          return;
        }
  
        const { isIntroductory, trialDays, onetimePriceId } = product;
        formData.payment_method_id = paymentMethod?.id;
        formData.trial_period_days = trialDays !== undefined && trialDays !== null
          ? String(trialDays)
          : undefined;
        if (isIntroductory && onetimePriceId) {
          formData.introductory_price_id = onetimePriceId;
        }
  
        await onSubmit(formData);
  
      } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
        await onSubmit(formData);
      }
    } catch (error) {
      // swallow — payment errors are surfaced via onError / the error popup
    } finally {
      setLoading(false);
    }
  };

  const handleApplePaySubmit = (e) => {
    onApplePaySubmit({
      ...e,
      paymentSystem,
    });
  };

  const handleGooglePaySubmit = (e) => {
    onGooglePaySubmit({
      ...e,
      paymentSystem,
    });
  };

  const handleCardElementChange = (e) => {
    if (isInvalidCardForm) {
      setInvalidCardForm(false);
    }

    if (typeof onChange === "function") {
      onChange(e);
    }
  };

  const handleSuccess = (e) => {
    onSuccess({
      ...e,
      paymentSystem,
    });
  };

  const handleError = (err) => {
    onError({
      ...(err || {}),
      paymentSystem,
    });
  };

  const renderField = (key, attributes) => {
    const {
      label,
      isValid,
      isTouched,
      value,
      type,
      placeholder = "",
    } = attributes;
    return (
      <div
        key={key}
        className={classNames(
          styles.field,
          {
            invalid: !isValid && isTouched,
          },
          fieldClassName
        )}
      >
        {label && <label htmlFor={`additional-field-${key}`}>{label}</label>}
        <input
          id={`additional-field-${key}`}
          data-recurly={key}
          name={key}
          placeholder={placeholder}
          value={value}
          type={type}
          onChange={handleInputChange}
        />
      </div>
    );
  };

  const renderFields = () => {
    const fieldsArray = Object.keys(fields);

    if (!fieldsArray.length) {
      return null;
    }

    const nameFields = fieldsArray.filter((key) => key === 'first_name' || key === 'last_name');
    const restFields = fieldsArray.filter((key) => !nameFields.includes(key));

    return (
      <>
        {restFields.map((key) => renderField(key, fields[key]))}
        {!!nameFields.length && (
          <div className={styles.inlineFields}>
            {nameFields.map((key) => renderField(key, fields[key]))}
          </div>
        )}
      </>
    );
  };

  const fromClassNames = classNames(styles.form, formClassName);

  const cardClassNames = classNames(
    styles.field,
    { [styles.invalid]: isInvalidCardForm },
    // Unscoped on purpose. The Stripe split fields live in their own CSS module,
    // and a hashed class can't be selected from there — this is what lets them
    // put the red border on the field that is actually empty. The name/email
    // fields below already use the same unscoped `invalid`.
    { invalid: isInvalidCardForm },
    { [styles.submitted]: submitted },
    fieldClassName
  );

  const buttonClassNames = classNames(
    styles.button,
    {
      disabled: isLoading,
    },
    buttonClassName
  );
  console.log(isLoading);
  return (
    <form onSubmit={handleSubmit} className={fromClassNames} ref={formRef}>
      <CardElement
        paymentSystem={paymentSystem}
        product={product}
        options={options}
        className={cardClassNames}
        onSubmit={handleSubmit}
        onChange={handleCardElementChange}
        onReady={onReady}
        onSuccess={handleSuccess}
        onError={handleError}
        onApplePayClick={onApplePayClick}
        onApplePaySubmit={handleApplePaySubmit}
        onGooglePayClick={onGooglePayClick}
        onGooglePaySubmit={handleGooglePaySubmit}
        onUserInfoChange={onUserInfoChange}
        onEvent={onEvent}
        inputStyles={inputStyles}
        labelStyles={labelStyles}
        notEmptyLabelStyles={notEmptyLabelStyles}
        paymentPopup={paymentPopup}
      />

      {renderFields()}

      {children}

      {paymentSystem !== PaymentSystem.PADDLE &&
        paymentSystem !== PaymentSystem.SOLIDGATE && (
          <button
            type="submit"
            disabled={isLoading}
            className={buttonClassNames}
          >
            {isLoading ? (
                <LoadingIcon width={20} height={20} />
            ) : (
              <span>{newPaymentModal ? <span>
                <LockIcon /> {t('onboarding.paymentModal.payByCard')}
              </span> : buttonTitle}</span>
            )}
          </button>
        )}
    </form>
  );
};

CardForm.propTypes = {
  product: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onReady: PropTypes.func,
  onApplePayClick: PropTypes.func,
  onApplePaySubmit: PropTypes.func,
  onGooglePayClick: PropTypes.func,
  onGooglePaySubmit: PropTypes.func,
  onUserInfoChange: PropTypes.func,
  onEvent: PropTypes.func,
  formClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  fieldClassName: PropTypes.string,
  buttonTitle: PropTypes.string,
  additionalFields: PropTypes.object,
  options: PropTypes.object,
};

CardForm.defaultProps = {
  buttonTitle: "Submit",
  additionalFields: {},
};

export default CardForm;
